import ReactGA from 'react-ga4';

/**
 * Initialize Google Analytics
 * @param measurementId Google Analytics measurement ID
 */
export const initGA = (measurementId: string) => {
  if (!import.meta.env.PROD) {
    console.log('Analytics tracking is disabled in development mode');
    return;
  }
  
  ReactGA.initialize(measurementId);
};

/**
 * Track page view with basic information
 * @param path Current page path including search params
 */
export const trackPageView = (path: string) => {
  if (!import.meta.env.PROD) return;
  
  ReactGA.send({ 
    hitType: 'pageview', 
    page: path 
  });
};

/**
 * Track page view with enhanced information
 * @param path Current page path including search params
 * @param title Page title
 * @param location Location object from React Router
 */
export const trackPageViewEnhanced = (
  path: string, 
  title?: string, 
  location?: { search: string }
) => {
  if (!import.meta.env.PROD) return;
  
  const pageTitle = title || document.title;
  const search = location?.search || '';
  
  ReactGA.send({
    hitType: 'pageview',
    page: path,
    title: pageTitle,
    location: path + search
  });
};

/**
 * Track event
 * @param category Event category 
 * @param action Event action
 * @param label Optional event label
 * @param value Optional event value
 */
export const trackEvent = (
  category: string, 
  action: string, 
  label?: string, 
  value?: number
) => {
  if (!import.meta.env.PROD) return;
  
  ReactGA.event({
    category,
    action,
    label,
    value
  });
};

/**
 * Track user timing
 * @param category Timer category
 * @param variable Timer variable name
 * @param value Time in milliseconds
 * @param label Optional label
 */
export const trackTiming = (
  category: string,
  variable: string,
  value: number,
  label?: string
) => {
  if (!import.meta.env.PROD) return;
  
  ReactGA.send({
    hitType: 'timing',
    timingCategory: category,
    timingVar: variable,
    timingValue: value,
    timingLabel: label
  });
};

/**
 * Set user ID for cross-device tracking
 * @param userId User ID
 */
export const setUserId = (userId: string) => {
  if (!import.meta.env.PROD) return;
  
  ReactGA.set({ userId });
};

/**
 * Add exception tracking
 * @param description Error description
 * @param fatal Whether the error was fatal
 */
export const trackException = (description: string, fatal: boolean = false) => {
  if (!import.meta.env.PROD) return;
  
  ReactGA.exception({
    description,
    fatal
  });
};